import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoSwitch } from "@/shared/components/RobotoSwitch";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { MessagePathNode, PathParser } from "@/shared/domain/topics";
import { docsUrl } from "@/shared/environment";
import { InfoOutlinedIcon } from "@/shared/foundation/icons/mui";
import { useDebouncedCallback } from "@/shared/hooks";
import {
  type PlotSeries,
  actions,
  useVizDispatch,
} from "@/shared/state/visualization";
import { seriesConfig } from "@/shared/state/visualization/identifiers";
import {
  DEFAULT_BREAK_LINE_AT_DISCONTINUITY,
  DEFAULT_LINE_WIDTH,
  DEFAULT_POINT_RADIUS,
} from "@/shared/state/visualization/plotDefaults";
import {
  PlotSeriesConfig as PlotSeriesConfigState,
  PlotYAxis,
} from "@/shared/state/visualization/schema/v1";

import { DataSourceSelector } from "../../../controls";
import { isViewableAsPlot } from "../../../Panel/predicates";
import { default as sectionStyles } from "../../SettingsSection.module.css";
import { Spotlight } from "../../Spotlight";
import { ConfigItem } from "../ConfigItem";

import styles from "./PlotSeriesConfig.module.css";
import { PlotSeriesHeader } from "./PlotSeriesHeader";
import { TransformField } from "./TransformField";

interface PlotSeriesConfigProps {
  allowedYAxes: number;
  displayLabel: string;
  isExpanded: boolean;
  onToggle: (seriesId: string) => void;
  panelId: string;
  plotSeries: PlotSeries;
  /** Scrolls the row into view and pulses it when it changes to a new truthy value. */
  spotlightKey?: number;
}

export function PlotSeriesConfig({
  allowedYAxes,
  displayLabel,
  isExpanded,
  onToggle,
  panelId,
  plotSeries,
  spotlightKey,
}: PlotSeriesConfigProps) {
  const fieldsId = React.useId();
  return (
    <Spotlight className={sectionStyles.setting} spotlightKey={spotlightKey}>
      <PlotSeriesHeader
        displayLabel={displayLabel}
        fieldsId={fieldsId}
        isExpanded={isExpanded}
        onToggle={onToggle}
        panelId={panelId}
        plotSeries={plotSeries}
      />
      <PlotSeriesConfigFields
        allowedYAxes={allowedYAxes}
        fieldsId={fieldsId}
        isExpanded={isExpanded}
        panelId={panelId}
        plotSeries={plotSeries}
      />
    </Spotlight>
  );
}

interface PlotSeriesConfigFieldsProps {
  allowedYAxes: number;
  fieldsId: string;
  isExpanded: boolean;
  panelId: string;
  plotSeries: PlotSeries;
}

/**
 * The fields below one series row's header, rendered only while the row is
 * expanded. Collapsed rows unmount the fields rather than hide them: the
 * fields are the bulk of the settings sidebar's DOM (several MUI text fields
 * plus three Autocompletes per series), and a panel plotting one signal
 * across many sessions holds one series per session, so the sidebar's open
 * cost grows with each mounted row. Unmounting also drops the text-field
 * state below: a collapsed row discards half-typed input and re-seeds from
 * the persisted config when reopened.
 */
function PlotSeriesConfigFields({
  isExpanded,
  ...fieldsProps
}: PlotSeriesConfigFieldsProps) {
  if (!isExpanded) {
    return null;
  }
  return <ExpandedSeriesFields {...fieldsProps} />;
}

function ExpandedSeriesFields({
  allowedYAxes,
  fieldsId,
  panelId,
  plotSeries,
}: Omit<PlotSeriesConfigFieldsProps, "isExpanded">) {
  const vizStateDispatch = useVizDispatch();

  const debouncedDispatch = useDebouncedCallback(function dispatch(
    updates: Partial<PlotSeriesConfigState>,
  ) {
    vizStateDispatch(actions.setSeriesConfig(panelId, plotSeries.id, updates));
  }, 15);
  const updateConfigLineWidth = (lineWidth: number | undefined) =>
    debouncedDispatch({ lineWidth });
  const updateConfigPointRadius = (pointRadius: number | undefined) =>
    debouncedDispatch({ pointRadius });
  const updateLabel = (label: string | undefined) =>
    debouncedDispatch({ label });

  const changeSeriesYAxis = useDebouncedCallback(function setSeriesYAxis(
    yAxis: PlotYAxis | undefined,
  ) {
    if (yAxis === "y") {
      yAxis = undefined;
    }
    vizStateDispatch(
      actions.setSeriesConfig(panelId, plotSeries.id, {
        yAxis,
      }),
    );
  }, 15);

  // A group carries no config of its own; its fields show the defaults.
  const config = seriesConfig(plotSeries);
  const [lineWidth, setLineWidth] = React.useState(
    `${config?.lineWidth ?? ""}`,
  );
  const [pointRadius, setPointRadius] = React.useState(
    `${config?.pointRadius ?? ""}`,
  );
  const yAxis = config?.yAxis;
  const breakLineAtDiscontinuity =
    config?.breakLineAtDiscontinuity ?? DEFAULT_BREAK_LINE_AT_DISCONTINUITY;

  const fileId = plotSeries.data.topic.association.association_id;

  const formattedMessagePath = MessagePathNode.partsToString(
    plotSeries.data.messagePath.parts,
  );
  const path = [plotSeries.data.topic.name, formattedMessagePath].join(
    PathParser.TOPIC_SEPARATOR,
  );

  const yAxisOptions: { value: PlotYAxis; label: string }[] = [
    { value: "y", label: "Default Y-Axis (Left)" },
  ];
  for (let i = 1; i < allowedYAxes; i++) {
    yAxisOptions.push({ value: `y${i}l`, label: `Y-Axis ${i} (Left)` });
    yAxisOptions.push({ value: `y${i}r`, label: `Y-Axis ${i} (Right)` });
  }

  return (
    <div id={fieldsId} className={styles.fields}>
      <ConfigItem label="Data Source">
        <DataSourceSelector
          initialFile={fileId}
          initialPath={path}
          onChange={(messagePathNode) => {
            vizStateDispatch(
              actions.setDataSource({
                topicData: messagePathNode.toTopicData(),
                panelId,
                segmentId: plotSeries.id,
              }),
            );
          }}
          optionFilter={isViewableAsPlot}
        />
      </ConfigItem>
      <ConfigItem label="Label">
        <TextField
          className={sectionStyles.input}
          defaultValue={config?.label ?? ""}
          label="Label"
          inputProps={{
            className: sectionStyles.inputLabel,
            "aria-label": `Label for ${path}`,
            maxLength: 200,
          }}
          InputProps={{
            className: sectionStyles.inputLabel,
          }}
          InputLabelProps={{
            className: sectionStyles.inputLabel,
            shrink: true,
          }}
          placeholder={path}
          margin="dense"
          size="small"
          fullWidth
          onChange={(event) => {
            const value = event.target.value;
            updateLabel(value || undefined);
          }}
        />
      </ConfigItem>
      <ConfigItem label="Style">
        <div className={sectionStyles.inputRow}>
          <TextField
            className={sectionStyles.input}
            value={lineWidth}
            label="Line Width"
            error={
              !!lineWidth && !Number.isFinite(Number.parseFloat(lineWidth))
            }
            inputProps={{
              className: sectionStyles.inputLabel,
              "aria-label": `Change line width for ${path}`,
              maxLength: 50,
            }}
            InputProps={{
              className: sectionStyles.inputLabel,
            }}
            InputLabelProps={{
              className: sectionStyles.inputLabel,
              shrink: true,
            }}
            margin="dense"
            onChange={(event) => {
              const value = event.target.value;
              setLineWidth(value);
              if (!value) {
                updateConfigLineWidth(undefined);
                return;
              }
              const parsed = Number.parseFloat(value);
              if (Number.isFinite(parsed)) {
                updateConfigLineWidth(parsed);
              }
            }}
            placeholder={`${DEFAULT_LINE_WIDTH}`}
            size="small"
          />
          <TextField
            className={sectionStyles.input}
            value={pointRadius}
            label="Point Radius"
            error={
              !!pointRadius && !Number.isFinite(Number.parseFloat(pointRadius))
            }
            inputProps={{
              className: sectionStyles.inputLabel,
              "aria-label": `Change point radius for ${path}`,
              maxLength: 50,
            }}
            InputProps={{
              className: sectionStyles.inputLabel,
            }}
            InputLabelProps={{
              className: sectionStyles.inputLabel,
              shrink: true,
            }}
            placeholder={`${DEFAULT_POINT_RADIUS}`}
            margin="dense"
            size="small"
            onChange={(event) => {
              const value = event.target.value;
              setPointRadius(value);
              if (!value) {
                updateConfigPointRadius(undefined);
                return;
              }
              const parsed = Number.parseFloat(value);
              if (Number.isFinite(parsed)) {
                updateConfigPointRadius(parsed);
              }
            }}
          />
        </div>
        <label className={styles.switchLabel}>
          Break line at null values
          <RobotoSwitch
            checked={breakLineAtDiscontinuity}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              vizStateDispatch(
                actions.setSeriesConfig(panelId, plotSeries.id, {
                  breakLineAtDiscontinuity: event.target.checked,
                }),
              );
            }}
            color="success"
          />
        </label>
      </ConfigItem>
      {allowedYAxes > 1 && (
        <ConfigItem label="Y Axis">
          <Autocomplete
            fullWidth
            disableClearable
            getOptionLabel={(option) => option.label}
            size="small"
            onChange={(_, selection) => {
              if (selection === null) {
                return;
              }
              changeSeriesYAxis(selection.value);
            }}
            options={yAxisOptions}
            value={
              yAxisOptions.find((option) => option.value === yAxis) ??
              yAxisOptions[0]
            }
            className={styles.yAxisSelect}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  className: sectionStyles.inputLabel,
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true,
                  className: sectionStyles.inputLabel,
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                className={classNames([
                  props.className,
                  sectionStyles.inputLabel,
                ])}
              >
                {option.label}
              </li>
            )}
          />
        </ConfigItem>
      )}
      <ConfigItem
        label={
          <>
            Y Transform
            <RobotoTooltip title="View supported functions">
              <a
                href={docsUrl("/user-guides/plot-transform-functions")}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View supported transform functions"
                className={styles.transformDocsLink}
              >
                <InfoOutlinedIcon className={styles.transformDocsLinkIcon} />
              </a>
            </RobotoTooltip>
          </>
        }
      >
        <TransformField
          panelId={panelId}
          seriesId={plotSeries.id}
          appliedExpression={config?.transform?.expression ?? ""}
          seriesLabel={path}
        />
      </ConfigItem>
    </div>
  );
}
